<template>
    <div>
        <v-alert v-if="error" color="error" icon="mdi-alert-octagon-outline" outlined text>
            {{error}}
        </v-alert>

        <v-data-table
            :headers="headers"
            :items="licenses"
            :loading="loading"
            :custom-sort="customSort"
            sort-by="key"
            class="licenses"
            dense
            hide-default-footer
            disable-pagination
        >
            <template #item.key="{ item }">
                <span class="key" @copy.prevent="copyKey(item.key, $event)">{{maskKey(item.key)}} <CopyButton :text="item.key"/></span>
            </template>
            <template #item.status="{ item }">
                <v-chip v-if="item.status === 'active'" label small outlined color="success">Active</v-chip>
                <v-chip v-else-if="item.status === 'inactive'" label small outlined color="warning">Inactive</v-chip>
                <v-chip v-else-if="item.status === 'disabled'" label small outlined>Archived</v-chip>
            </template>
            <template #item.name="{ item }">
                <v-text-field v-if="edit === item.key" v-model="item.name" hide-details dense outlined>
                    <template #append>
                        <v-btn icon small @click="post({action: 'save', key: item.key, name: item.name})">
                            <v-icon small>mdi-check</v-icon>
                        </v-btn>
                    </template>
                </v-text-field>
                <template v-else>
                    {{item.name}}
                    <v-btn icon small @click="edit = item.key"><v-icon small>mdi-pencil</v-icon></v-btn>
                </template>
            </template>
            <template #item.deployments="{ item }">
                {{item.deployments || '—'}}
            </template>
            <template #item.nodes="{ item }">
                {{item.nodes || '—'}}
            </template>
            <template #item.cores="{ item }">
                {{item.cores || '—'}}
            </template>
            <template #item.actions="{ item }">
                <div v-if="archive === item.key" class="py-1">
                    <div class="text-no-wrap">Confirm archiving license</div>
                    <v-btn x-small color="error" class="ml-1" @click="post({action: 'disable', key: item.key})">Confirm</v-btn>
                    <v-btn x-small class="ml-1" @click="archive = ''">Cancel</v-btn>
                </div>
                <v-tooltip v-else bottom>
                    <template #activator="{ on }">
                        <v-btn icon small v-on="on" :disabled="item.status === 'disabled'" @click="archive = item.key">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </template>
                    <v-card class="py-1 px-3">
                        archive
                    </v-card>
                </v-tooltip>
            </template>
        </v-data-table>

        <div v-if="!licenses.length && !loading" class="my-4 grey--text text-center font-size-small">
            It seems that you don't have any licenses yet
        </div>

        <div class="mt-2">
            <v-btn v-if="!loading && !licenses.length" class="primary" @click="post({action: 'add'})">Get Free Trial License</v-btn>
            <v-btn v-if="licenses.length" class="primary" @click="post({action: 'add'})">Get New License</v-btn>
        </div>

        <p v-if="licenses.length" class="mt-4">
            Please refer to the <a href="https://docs.coroot.com/quick-start/enterprise" target="_blank">documentation</a> to deploy Coroot Enterprise.
        </p>
    </div>
</template>

<script>
import CopyButton from "@/components/CopyButton.vue";

export default {
    props: {
        organizationId: String,
    },

    components: {CopyButton},

    data() {
        return {
            loading: false,
            error: '',
            licenses: [],
            edit: '',
            archive: '',
            headers: [
                { text: 'License Key', value: 'key' },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Description', value: 'name', cellClass: 'description-cell' },
                { text: 'Coroot Deployments', value: 'deployments' },
                { text: 'Nodes', value: 'nodes' },
                { text: 'CPU cores', value: 'cores' },
                { text: '', value: 'actions', sortable: false, align: 'end' },
            ],
        };
    },

    mounted() {
        this.get();
    },

    methods: {
        maskKey(key) {
            const parts = key.split('-');
            if (parts.length >= 5) {
                return `${parts[0]}-${parts[1]}-***-${parts[parts.length - 1]}`;
            }
            return key;
        },
        copyKey(key, event) {
            event.clipboardData.setData('text/plain', key);
        },
        customSort(items, sortBy, sortDesc) {
            const statusOrder = { active: 1, inactive: 2, disabled: 3 };
            return items.slice().sort((a, b) => {
                // First sort by status
                const statusDiff = statusOrder[a.status] - statusOrder[b.status];
                if (statusDiff !== 0) return statusDiff;

                // Then by the selected column
                const key = sortBy[0];
                const desc = sortDesc[0];
                if (!key) return 0;

                let cmp = 0;
                if (typeof a[key] === 'string') {
                    cmp = a[key].localeCompare(b[key]);
                } else {
                    cmp = (a[key] || 0) - (b[key] || 0);
                }
                return desc ? -cmp : cmp;
            });
        },
        get() {
            this.loading = true;
            this.error = '';
            this.$api.licenses(this.organizationId, null,(data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                this.licenses = data || [];
            });
        },
        post(form) {
            this.loading = true;
            this.error = '';
            this.edit = '';
            this.archive = '';
            const refresh = !this.licenses.length;
            this.$api.licenses(this.organizationId, form,(data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                this.$st.send('license-'+form.action, null);
                if (refresh) {
                    this.$emit('refresh');
                }
                this.get();
            });
        },
    },
}
</script>

<style scoped>
.licenses:deep(tr) {
    background-color: unset !important;
}
.licenses:deep(th) {
    white-space: nowrap;
}
.licenses .key {
    font-family: monospace, monospace;
    white-space: nowrap;
}
.licenses:deep(.description-cell) {
    max-width: 200px;
    word-break: break-word;
}
.licenses:deep(.v-input) {
    font-size: inherit !important;
}
.licenses:deep(.v-input__slot) {
    min-height: 30px !important;
    padding-right: 4px !important;
}
.licenses:deep(.v-input__append-inner) {
    margin-top: 0 !important;
}
.licenses:deep(input) {
    padding: 4px 0 !important;
}
</style>
